import { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Popover } from '@headlessui/react';
import { NavLink, useLocation } from 'react-router-dom';
import BadgeV3 from '../../badge/BadgeV3';
import { TooltipV2 } from '../..//tooltip/TooltipV2';
import { motion, AnimatePresence } from 'framer-motion';

type badgeColorOption = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'violet' | 'indigo' | 'pink' | 'orange';

type VerticalNavTabProps = {
    // tab label text
    label: string;

    // child elements displayed in popover-type tabs
    children?: ReactNode[] | ReactNode;

    // destination path for navtabs
    link?: string;

    // badge props (label and color type)
    badge?: string;
    badgeColor?: badgeColorOption;

    // disabled props (state and tooltip label)
    isDisabled?: boolean;
    isDisabledHint?: string;

    close: () => void;
};

const styles = {
    tab: {
        default: {
            unselected:
                'border-transparent rounded-md text-gray-500 hover:bg-indigo-50 whitespace-nowrap border-b-2 w-full font-medium text-sm cursor-pointer group inline-flex items-center cursor:pointer px-5 py-2 dark:hover:bg-indigo-900 dark:text-gray-400 outline-none',
            selected: 'bg-indigo-50 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-100',
            disabled: 'opacity-75 cursor-not-allowed border-none dark:border-none hover:bg-transparent',
        },
    },
};

function VerticalNavTab({ label, children, link, badge, badgeColor, isDisabled, isDisabledHint, close }: VerticalNavTabProps): JSX.Element {
    const location = useLocation();
    const isSelected = location.pathname.includes(link);

    return (
        <TooltipV2 disabled={!isDisabled} message={isDisabledHint} position="right">
            {children ? (
                <NavLink onClick={(e): void => e.preventDefault()} to={isDisabled ? '#' : link}>
                    <Popover className="w-full relative">
                        {({ open }): JSX.Element => (
                            <>
                                <Popover.Button
                                    className={`${styles.tab.default.unselected} ${
                                        !isDisabled && (isSelected || open) ? styles.tab.default.selected : ''
                                    } ${isDisabled && styles.tab.default.disabled} outline-none`}
                                >
                                    <span className={`${badge && 'mr-2'}`}>{label}</span>
                                    {badge && <BadgeV3 color={badgeColor} rounded label={badge} />}
                                    <ChevronRightIcon className="w-5 h-5 ml-auto" />
                                </Popover.Button>
                                <AnimatePresence initial={false}>
                                    {children && !isDisabled && open && (
                                        <Popover.Panel
                                            static
                                            as={motion.div}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                            transition={{ duration: 0.1 }}
                                            className="absolute border-[1px] shadow-md rounded-md p-3 left-full ml-5 top-0 bg-white dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 cursor-default"
                                        >
                                            {children}
                                        </Popover.Panel>
                                    )}
                                </AnimatePresence>
                            </>
                        )}
                    </Popover>
                </NavLink>
            ) : (
                <NavLink
                    className={`${styles.tab.default.unselected} ${!isDisabled && isSelected && styles.tab.default.selected} ${
                        isDisabled && styles.tab.default.disabled
                    }`}
                    to={isDisabled ? '#' : link}
                    onClick={close}
                >
                    <span className={`${badge && 'mr-2'}`}>{label}</span> {badge && <BadgeV3 color={badgeColor} rounded label={badge} />}
                </NavLink>
            )}
        </TooltipV2>
    );
}

export default VerticalNavTab;
